import { useState, useEffect } from "react";
import PageHeader from "@/components/PageHeader";
import HomeHealthScore from "@/components/HomeHealthScore";
import ActionCard from "@/components/ActionCard";
import FloatingActionButton from "@/components/FloatingActionButton";
import LogMaintenanceSheet from "@/components/LogMaintenanceSheet";
import GlassCard from "@/components/GlassCard";
import ActionDetailModal from "@/components/ActionDetailModal";
import OnboardingModal from "@/components/OnboardingModal";
import NewFeaturesGuide from "@/components/NewFeaturesGuide";
import PlantActionCard from "@/components/PlantActionCard";
import { useProfile } from "@/hooks/useProfile";
import AskHomeEzi from "@/components/AskHomeEzi";
import { useAppliances } from "@/hooks/useAppliances";
import { usePlants } from "@/hooks/usePlants";
import { useFurniture } from "@/hooks/useFurniture";
import { useAuth } from "@/contexts/AuthContext";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { ChevronRight, Loader2, Lock, Droplets } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useSubscription } from "@/hooks/useSubscription";
import UpgradePlanSheet from "@/components/UpgradePlanSheet";
import HomeHealthReportSheet from "@/components/HomeHealthReportSheet";
import PendingInvites from "@/components/PendingInvites";
import { usePropertySharing } from "@/hooks/usePropertySharing";

import { calculateNextServiceDate } from "@/lib/dateUtils";
import { useRoutineTasks } from "@/hooks/useRoutineTasks";

const Index = () => {
    const { session, userProfile: authProfile } = useAuth();
    const user = session?.user;

    const { selectedProperty, properties, setActiveProperty, loading: propertyLoading } = usePropertyContext();
    const activeProperty = selectedProperty;

    const { profile, loading: profileLoading } = useProfile();
    const activeProfile = profile || authProfile;
    const activeProfileLoading = profileLoading && !authProfile;

    const { appliances, loading: appliancesLoading, getUpcomingServiceAppliances, getUpcomingSubTasks, updateAppliance, getExpiringWarranties: getApplianceWarranties, dismissWarranty: dismissApplianceWarranty, refetch: refetchAppliances } = useAppliances(activeProperty?.id);
    const { plants, loading: plantsLoading, markAsWatered } = usePlants(activeProperty?.id);
    const { furniture, getExpiringWarranties: getFurnitureWarranties, dismissWarranty: dismissFurnitureWarranty } = useFurniture(activeProperty?.id);
    const { addRoutineTask } = useRoutineTasks();
    const [isLogSheetOpen, setIsLogSheetOpen] = useState(false);
    const [logApplianceId, setLogApplianceId] = useState(null);
    const [prefilledLogDescription, setPrefilledLogDescription] = useState("");
    const [logSubTaskId, setLogSubTaskId] = useState(null);
    const [isChatFocused, setIsChatFocused] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showFeaturesGuide, setShowFeaturesGuide] = useState(false);
    const [showUpgradeSheet, setShowUpgradeSheet] = useState(false);
    const { isFeatureLocked, plan, isPropertyLocked } = useSubscription();
    const propertyLocked = activeProperty ? isPropertyLocked(activeProperty.id) : false;

    const handleLogClick = () => {
        setLogApplianceId(null);
        setPrefilledLogDescription("");
        setLogSubTaskId(null);
        setIsLogSheetOpen(true);
    };

    const handleLogService = (item) => {
        setLogApplianceId(item.type === 'appliance-subtask' ? item.applianceId : item.id);
        setPrefilledLogDescription(item.type === 'appliance-subtask' ? item.subTaskName : "");
        setLogSubTaskId(item.type === 'appliance-subtask' ? item.subTaskId : null);
        setIsLogSheetOpen(true);
    };

    const handleMarkSubTaskDone = async (applianceId, subTaskId, subTaskName) => {
        const applianceItem = appliances.find(a => a.id === applianceId);
        if (!applianceItem) return;

        const updatedTasks = (applianceItem.maintenance_tasks || []).map(task => {
            if (task.id === subTaskId) {
                const todayStr = new Date().toISOString().split("T")[0];
                const nextDate = calculateNextServiceDate(todayStr, task.frequency, task.frequency_unit);
                return {
                    ...task,
                    last_service_date: todayStr,
                    next_service_date: nextDate
                };
            }
            return task;
        });

        await updateAppliance(applianceId, { maintenanceTasks: updatedTasks });

        await addRoutineTask({
            property_id: activeProperty?.id,
            item_type: 'appliance',
            item_id: applianceId,
            task_name: subTaskName,
            completed_at: new Date().toISOString()
        });

        toast({
            title: "Sub-task Completed",
            description: `"${subTaskName}" for ${applianceItem.brand || ""} ${applianceItem.name} marked as complete.`,
        });

        refetchAppliances();
    };

    const handleDismissWarranty = async (e, item) => {
        e.stopPropagation(); // prevent opening the ActionDetailModal
        const originalId = item.originalId;
        const isFurniture = item.category !== undefined && furniture.some(f => f.id === originalId);

        if (isFurniture) {
            await dismissFurnitureWarranty(originalId);
        } else {
            await dismissApplianceWarranty(originalId);
        }
    };

    // Generate dynamic action items from appliances due for service
    const applianceActionItems = getUpcomingServiceAppliances().map((appliance) => {
        const nextService = new Date(appliance.next_service_date);
        const today = new Date();
        nextService.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const diffTime = nextService.getTime() - today.getTime();
        const daysUntil = Math.round(diffTime / (1000 * 60 * 60 * 24));

        let urgency = "low";
        let dueText = "";

        if (daysUntil < 0) {
            urgency = "high";
            dueText = `Overdue by ${Math.abs(daysUntil)} days`;
        } else if (daysUntil <= 7) {
            urgency = "high";
            dueText = daysUntil === 0 ? "Due today" : `Due in ${daysUntil} days`;
        } else if (daysUntil <= 14) {
            urgency = "medium";
            dueText = `Due in ${daysUntil} days`;
        } else {
            urgency = "low";
            dueText = `Due in ${Math.ceil(daysUntil / 7)} weeks`;
        }

        return {
            id: appliance.id,
            title: `Service ${appliance.brand || ""} ${appliance.name}`.trim(),
            category: appliance.category,
            dueDate: appliance.next_service_date,
            dueText,
            urgency,
            type: "appliance"
        };
    });

    const plantActionItems = plants
        .filter(plant => {
            if (plant.is_paused) return false;
            const nextWater = new Date(plant.next_watering_at);
            const today = new Date();
            nextWater.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            return nextWater <= today;
        })
        .map(plant => {
            const nextWater = new Date(plant.next_watering_at);
            const today = new Date();
            nextWater.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);

            const diffTime = nextWater.getTime() - today.getTime();
            const daysUntil = Math.round(diffTime / (1000 * 60 * 60 * 24));

            let dueText = daysUntil < 0 ? `Overdue by ${Math.abs(daysUntil)} days` : "Due today";

            return {
                id: `plant-${plant.id}`,
                originalId: plant.id,
                title: `Water ${plant.name}`.trim(),
                category: "plant",
                dueDate: plant.next_watering_at,
                dueText,
                urgency: "high",
                type: "plant",
                plant: plant
            };
        });

    const warrantyActionItems = [...(getApplianceWarranties ? getApplianceWarranties() : []), ...(getFurnitureWarranties ? getFurnitureWarranties() : [])].map(item => {
        const expiryDate = new Date(item.warranty_expiry);
        const today = new Date();
        expiryDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const diffTime = expiryDate.getTime() - today.getTime();
        const daysUntil = Math.round(diffTime / (1000 * 60 * 60 * 24));

        let urgency = "high";
        let dueText = "";

        if (daysUntil < 0) {
            dueText = `Expired ${Math.abs(daysUntil)} days ago`;
        } else if (daysUntil === 0) {
            dueText = "Expires today";
        } else if (daysUntil <= 7) {
            dueText = `Expires in ${daysUntil} days`;
        } else if (daysUntil <= 14) {
            urgency = "medium";
            dueText = `Expires in ${daysUntil} days`;
        } else {
            urgency = "low";
            dueText = `Expires in ${Math.ceil(daysUntil / 7)} weeks`;
        }

        return {
            id: `warranty-${item.id}`,
            originalId: item.id,
            title: `Warranty: ${item.brand || ""} ${item.name}`.trim(),
            category: item.category || "furniture",
            dueDate: item.warranty_expiry,
            dueText,
            urgency,
            type: "warranty",
            item: item
        };
    });

    // Generate dynamic action items from sub-tasks due for service.
    // Fix: getUpcomingSubTasks() already applies the same 30-day window as
    // applianceActionItems (via getUpcomingServiceAppliances()) — this used
    // to re-filter the result down to "next_service <= today" only, so a
    // sub-task due in, say, 10 days would never show up here even though the
    // matching top-level appliance service due in 10 days would. Dropping
    // the redundant today-only filter makes the two lists use the same window.
    const subTaskActionItems = getUpcomingSubTasks()
        .map((task) => {
        const nextService = new Date(task.next_service_date);
        const today = new Date();
        nextService.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const diffTime = nextService.getTime() - today.getTime();
        const daysUntil = Math.round(diffTime / (1000 * 60 * 60 * 24));

        let urgency = "low";
        let dueText = "";

        if (daysUntil < 0) {
            urgency = "high";
            dueText = `Overdue by ${Math.abs(daysUntil)} days`;
        } else if (daysUntil <= 7) {
            urgency = "high";
            dueText = daysUntil === 0 ? "Due today" : `Due in ${daysUntil} days`;
        } else if (daysUntil <= 14) {
            urgency = "medium";
            dueText = `Due in ${daysUntil} days`;
        } else {
            urgency = "low";
            dueText = `Due in ${Math.ceil(daysUntil / 7)} weeks`;
        }

        const applianceLabel = `${task.applianceBrand || ""} ${task.applianceName}`.trim();

        return {
            id: `subtask-${task.applianceId}-${task.id}`,
            applianceId: task.applianceId,
            subTaskId: task.id,
            subTaskName: task.name,
            title: `${applianceLabel}: ${task.name}`,
            category: task.category,
            dueDate: task.next_service_date,
            dueText,
            urgency,
            type: "appliance-subtask"
        };
    });

    const actionItems = [...applianceActionItems, ...subTaskActionItems, ...plantActionItems, ...warrantyActionItems].sort((a, b) => {
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    });


    const CURRENT_GUIDE_VERSION = 4;

    // 1. Detect return from Paystack and poll until plan updates
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const hasTrxref = queryParams.has('trxref') || queryParams.has('reference');
        if (!hasTrxref) return;

        if (!user?.id) return;

        // Clear query parameters from URL immediately so they don't persist on refresh or re-render
        window.history.replaceState({}, document.title, window.location.pathname);

        // Only show toast on the first mount, not on subsequent renders due to profile update
        if (!window.sessionStorage.getItem('paystack_toast_shown_index')) {
            toast({
                title: "Payment Received",
                description: "We're activating your plan now...",
            });
            window.sessionStorage.setItem('paystack_toast_shown_index', 'true');
        }

        let attempts = 0;
        const MAX_ATTEMPTS = 10;
        const INTERVAL_MS = 3000;

        const pollInterval = setInterval(async () => {
            attempts += 1;

            try {
                const { data } = await supabase
                    .from('profiles')
                    .select('subscription_plan')
                    .eq('id', user.id)
                    .maybeSingle();

                if (data?.subscription_plan && data.subscription_plan !== 'free') {
                    clearInterval(pollInterval);
                    refetchAppliances();
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('refetch-profile'));
                    }
                    return;
                }
            } catch (err) {
                console.error("Failed to poll profile status:", err);
            }

            refetchAppliances();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('refetch-profile'));
            }

            if (attempts >= MAX_ATTEMPTS) {
                clearInterval(pollInterval);
            }
        }, INTERVAL_MS);

        return () => clearInterval(pollInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, refetchAppliances]);

    // 2. Features Guide Version Logic (DB-based persistence for existing users)
    useEffect(() => {
        if (!activeProfile || activeProfileLoading || !user) {
            console.log("Features Guide Check: Skipped", { activeProfile, activeProfileLoading, hasUser: !!user });
            return;
        }

        // ONLY show if they are already onboarded AND have already seen the Welcome Guide.
        const hasSeenWelcome = (localStorage.getItem(`homeezi_welcome_${user?.id}`) === "true") || (activeProfile.is_onboarded === true);

        // Sync local storage if DB already indicates they have seen this version
        if (activeProfile.guide_version >= CURRENT_GUIDE_VERSION) {
            localStorage.setItem(`homeezi_guide_seen_${user.id}_${CURRENT_GUIDE_VERSION}`, "true");
        }

        // Re-read localStorage AFTER syncing so the value is always up-to-date
        const hasSeenGuide = localStorage.getItem(`homeezi_guide_seen_${user.id}_${CURRENT_GUIDE_VERSION}`) === "true";

        console.log("Features Guide Check: Evaluating", {
            email: user.email,
            profile_guide_version: activeProfile.guide_version,
            CURRENT_GUIDE_VERSION,
            is_onboarded: activeProfile.is_onboarded,
            hasSeenWelcome,
            hasSeenGuide,
            showGuideCondition: activeProfile.is_onboarded && hasSeenWelcome && !hasSeenGuide && (activeProfile.guide_version || 0) < CURRENT_GUIDE_VERSION
        });

        if (activeProfile.is_onboarded && hasSeenWelcome && !hasSeenGuide && (activeProfile.guide_version || 0) < CURRENT_GUIDE_VERSION) {
            setShowFeaturesGuide(true);
        } else {
            setShowFeaturesGuide(false);
        }
    }, [activeProfile, activeProfileLoading, user]);



    const handleFeatureGuideClose = async (open) => {
        setShowFeaturesGuide(open);
        if (!open && user?.id) {
            // Set in localStorage immediately for instant state preservation
            localStorage.setItem(`homeezi_guide_seen_${user.id}_${CURRENT_GUIDE_VERSION}`, "true");

            // Update DB so they never see it again on this version
            await supabase
                .from('profiles')
                .update({ guide_version: CURRENT_GUIDE_VERSION })
                .eq('id', user.id);

            // Dispatch refetch event to update AuthContext state
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('refetch-profile'));
            }
        }
    };

    // 3. Onboarding Logic
    const { checkMyInvites } = usePropertySharing();
    const [hasPendingInvites, setHasPendingInvites] = useState(false);
    const [checkingInvites, setCheckingInvites] = useState(true);

    useEffect(() => {
        const check = async () => {
            if (!user) return;
            try {
                const invites = await checkMyInvites();
                setHasPendingInvites(invites && invites.length > 0);
            } catch (e) {
                console.error(e);
            } finally {
                setCheckingInvites(false);
            }
        }
        check();
    }, [user, checkMyInvites]);

    useEffect(() => {
        // Wait for all critical data to load before making a decision
        if (!user || propertyLoading || checkingInvites || activeProfileLoading) return;

        // Ensure we don't show the onboarding modal if the features guide is actively showing
        if (showFeaturesGuide) return;

        // Sync welcome walkthrough status to local storage if DB says they are onboarded
        if (activeProfile?.is_onboarded && localStorage.getItem(`homeezi_welcome_${user?.id}`) !== "true") {
            localStorage.setItem(`homeezi_welcome_${user?.id}`, "true");
        }

        // Check if they have already seen the walkthrough (or if they are already onboarded in DB)
        const hasSeenWalkthrough = (localStorage.getItem(`homeezi_welcome_${user?.id}`) === "true") || (activeProfile?.is_onboarded === true);

        // Show the welcome walkthrough if they haven't seen it
        if (!hasSeenWalkthrough && !hasPendingInvites) {
            setShowOnboarding(true);
        } else {
            setShowOnboarding(false);
        }
    }, [user, propertyLoading, activeProfileLoading, properties, showFeaturesGuide, activeProfile, hasPendingInvites, checkingInvites]);

    const handleActionClick = (item) => {
        setSelectedAction(item);
        setIsActionModalOpen(true);
    };

    const loading = activeProfileLoading || appliancesLoading || propertyLoading || plantsLoading;
    const [dynamicHealthScore, setDynamicHealthScore] = useState(null);

    // Fetch dynamic health score from Supabase View
    useEffect(() => {
        const fetchHealthScore = async () => {
            if (!activeProperty?.id) return;

            const { data, error } = await supabase
                .from("property_health_scores")
                .select("health_score")
                .eq("property_id", activeProperty.id)
                .maybeSingle();

            if (!error && data) {
                setDynamicHealthScore(data.health_score);
            }
        };

        fetchHealthScore();
    }, [activeProperty?.id, appliances]);

    const healthScore = dynamicHealthScore ?? 100;
    const houseName = activeProperty?.name || activeProperty?.address || "My Home";

    return (
        <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] pb-24">
            <PageHeader pageName="My Dashboard" houseName={houseName} />

            <main className="px-6" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 8rem)" }}>
                <div className="max-w-lg mx-auto space-y-6">
                    <PendingInvites />

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        </div>
                    ) : propertyLocked ? (
                        <GlassCard className="p-8 text-center flex flex-col items-center justify-center space-y-6 border border-amber-500/20 bg-amber-500/5 backdrop-blur-md">
                            <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mb-2">
                                <Lock className="w-8 h-8 text-amber-600" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-xl font-bold text-foreground">Property Locked</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
                                    Your plan allows managing up to 1 house. Please upgrade your subscription to access this property.
                                </p>
                            </div>
                            <div className="flex flex-col w-full gap-3">
                                <Button onClick={() => setShowUpgradeSheet(true)} className="w-full rounded-2xl py-6 font-bold bg-primary shadow-lg shadow-primary/20">
                                    Upgrade Plan
                                </Button>
                                {(() => {
                                    const firstUnlocked = properties.find(p => !isPropertyLocked(p.id));
                                    if (firstUnlocked) {
                                        return (
                                            <Button
                                                variant="outline"
                                                onClick={async () => {
                                                    await setActiveProperty(firstUnlocked.id);
                                                    if (typeof window !== 'undefined') window.location.reload();
                                                }}
                                                className="w-full rounded-2xl py-6 font-semibold"
                                            >
                                                Switch to {firstUnlocked.name}
                                            </Button>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                        </GlassCard>
                    ) : (
                        <>
                            <GlassCard className="p-4 pt-6">
                                <HomeHealthScore score={healthScore} />
                            </GlassCard>

                            <section>
                                <h2 className="text-lg font-semibold text-foreground mb-4">
                                    Recommended Actions
                                </h2>
                                {actionItems.length > 0 ? (
                                    <div className="space-y-3">
                                        {actionItems.map((item) => (
                                            item.type === 'plant' ? (
                                                <PlantActionCard
                                                    key={item.id}
                                                    plant={item.plant}
                                                    onMarkWatered={markAsWatered}
                                                    onClick={() => handleActionClick(item)}
                                                />
                                            ) : (
                                                <div
                                                    key={item.id}
                                                    onClick={() => handleActionClick(item)}
                                                    className="w-full text-left cursor-pointer transition-transform active:scale-[0.98]"
                                                >
                                                    <ActionCard
                                                        title={item.title}
                                                        dueText={item.dueText}
                                                        urgency={item.urgency}
                                                        actionButton={
                                                            <div className="flex items-center gap-2">
                                                                {item.type === 'warranty' && (
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={(e) => handleDismissWarranty(e, item)}
                                                                        className="h-7 text-xs font-semibold rounded-lg bg-background"
                                                                    >
                                                                        Dismiss
                                                                    </Button>
                                                                )}
                                                                <ChevronRight className="w-5 h-5 text-muted-foreground pointer-events-none" />
                                                            </div>
                                                        }
                                                    />
                                                </div>
                                            )
                                        ))}
                                    </div>
                                ) : (
                                    <GlassCard className="p-6 flex flex-col items-center justify-center text-center space-y-2 border-dashed border-2 bg-emerald-500/5 border-emerald-500/20">
                                        <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center mb-1">
                                            <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="text-sm font-semibold text-slate-900">All up to date</p>
                                        <p className="text-xs text-muted-foreground max-w-[200px]">No outstanding maintenance actions required for your assets.</p>
                                    </GlassCard>
                                )}
                            </section>

                            <section className="pt-2">
                                <h2 className="text-lg font-semibold text-foreground mb-4">
                                    Home Assistant
                                </h2>
                                <AskHomeEzi mode="embedded" onInputFocus={setIsChatFocused} />
                            </section>
                        </>
                    )}
                </div>
            </main>

            {!isChatFocused && !propertyLocked && (
                <FloatingActionButton
                    onClick={handleLogClick}
                    className={cn(isFeatureLocked('maintenance_history') && "bg-amber-500 hover:bg-amber-600 shadow-amber-200")}
                />
            )}
             <LogMaintenanceSheet
                open={isLogSheetOpen}
                onOpenChange={setIsLogSheetOpen}
                applianceId={logApplianceId}
                prefilledDescription={prefilledLogDescription}
                onSuccess={() => {
                    if (logSubTaskId) {
                        handleMarkSubTaskDone(logApplianceId, logSubTaskId, prefilledLogDescription);
                    } else {
                        refetchAppliances();
                    }
                }}
            />
            <ActionDetailModal
                open={isActionModalOpen}
                onOpenChange={setIsActionModalOpen}
                item={selectedAction}
                onLogService={handleLogService}
                onMarkWatered={markAsWatered}
                onMarkSubTaskDone={handleMarkSubTaskDone}
            />
            <OnboardingModal
                open={showOnboarding}
                onOpenChange={setShowOnboarding}
                onComplete={() => setShowOnboarding(false)}
            />
            <NewFeaturesGuide
                open={showFeaturesGuide}
                onOpenChange={handleFeatureGuideClose}
            />
            <UpgradePlanSheet
                open={showUpgradeSheet}
                onOpenChange={setShowUpgradeSheet}
                currentPlan={plan}
            />

        </div>
    );
};

export default Index;
